import CloseIcon from "@mui/icons-material/Close";
import { DrawerProps, Theme, useMediaQuery } from "@mui/material";
import Divider from "@mui/material/Divider";
import MuiLink from "@mui/material/Link";
import List from "@mui/material/List";
import { useEffect, useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import Talk2BuyLogo from "@/assets/logos/full-logo-secondary.png";
import Talk2BuySmallLogo from "@/assets/logos/small-logo.png";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { SUBSCRIBE_PERMISSIONS } from "@/constants/modules.permissions";
import { PWA_ALLOWED_MENU_KEYS, PWA_ALLOWED_SUBMENU_KEYS } from "@/constants/pwa-menu.constants";
import {
  defaultPageNumber as pageNumber,
  defaultPageSize as pageSize,
} from "@/constants/pagination";
import useAuth from "@/contexts/auth-context";
import { setMiniSidenav, useMaterialUIController } from "@/contexts/mui-context";
import useGetAllCustomerSalesProducts from "@/hooks/requests/customer-sales-product/use-get-all-customer-sales-product";
import { MenuItem, NestedCollapseItem } from "@/routes";
import { isPWAStandalone } from "@/utils/environment.utils";
import { CustomerSalesProductStatus as Status } from "@/types/customer-sales-product.types";

import SidenavCollapse from "./SidenavCollapse";
import SidenavItem from "./SidenavItem";
import SidenavList from "./SidenavList";
import SidenavRoot from "./SidenavRoot";
import {
  ensureOpenCollapse,
  ensureOpenNestedCollapse,
  getOpenCollapses,
  getOpenNestedCollapses,
  toggleOpenCollapse,
  toggleOpenNestedCollapse,
} from "./sidenav-open-state";

type SidenavProps = DrawerProps & {
  color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  routes: MenuItem[];
};

function Sidenav({ color = "dark", routes, ...rest }: SidenavProps) {
  const { data: contractedPlans } = useGetAllCustomerSalesProducts({ pageNumber, pageSize });

  const { user, checkPermissions } = useAuth();
  const hasNoPermission = checkPermissions(SUBSCRIBE_PERMISSIONS.CREATE);
  const hasNoProducts = contractedPlans?.items.length === 0;
  const hasActiveProducts =
    contractedPlans?.items.some(
      (item) => item.status === Status.Active || item.status === Status.HoldDuePaymentIssue
    ) ?? false;
  const showContract = !hasNoPermission && (hasNoProducts || !hasActiveProducts);

  // Memoizar filtro de rotas para evitar recriações desnecessárias
  const filteredRoutes = useMemo(() => {
    const isPWAMobile = isPWAStandalone() && window.innerWidth < 992;

    const filtered = routes.filter((route) => {
      if (route.type === "collapse" && route.code === "PLANS") {
        return showContract;
      }

      // No PWA mobile/tablet, exibir apenas itens permitidos
      if (isPWAMobile) {
        if (route.type === "collapse") {
          return PWA_ALLOWED_MENU_KEYS.includes(route.key);
        }
        // Manter títulos/divisores apenas se o próximo collapse é permitido
        if (route.type === "title" || route.type === "divider") {
          const routeIndex = routes.indexOf(route);
          const nextCollapse = routes.slice(routeIndex + 1).find((r) => r.type === "collapse");
          return !!nextCollapse && PWA_ALLOWED_MENU_KEYS.includes(nextCollapse.key);
        }
      }

      return true;
    });

    // No PWA mobile/tablet, também filtrar sub-itens (nested) para mostrar apenas os permitidos
    if (!isPWAMobile) return filtered;

    return filtered.map((route) => {
      if (route.type !== "collapse" || !route.collapse) return route;
      return {
        ...route,
        collapse: route.collapse.filter((sub) => PWA_ALLOWED_SUBMENU_KEYS.includes(sub.key)),
      };
    });
  }, [routes, showContract]);

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

  const location = useLocation();
  const pathname = location.pathname;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1, -1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];
  const [openCollapses, setOpenCollapses] = useState<string[]>(() => getOpenCollapses(collapseName));
  const [openNestedCollapses, setOpenNestedCollapses] = useState<string[]>(() =>
    getOpenNestedCollapses(itemParentName)
  );

  const toggleCollapse = (key: string) => {
    setOpenCollapses(toggleOpenCollapse(key));
  };

  const toggleNestedCollapse = (key: string) => {
    setOpenNestedCollapses(toggleOpenNestedCollapse(key));
  };

  // Função auxiliar para verificar se uma rota está ativa
  const isRouteActive = (routePath: string | undefined): boolean => {
    if (!routePath) return false;
    // Remove a linguagem do final do pathname para comparação
    const pathWithoutLang = pathname.split("/").slice(0, -1).join("/");
    const routeWithoutLang = routePath.split("/").slice(0, -1).join("/");
    return pathWithoutLang.startsWith(routeWithoutLang);
  };
  const is_xs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));

  let textColor: "inherit" | "dark" | "white" = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // Memoizar a logo para evitar re-renderizações desnecessárias
  const logoSrc = useMemo(() => {
    return miniSidenav && !is_xs ? Talk2BuySmallLogo : Talk2BuyLogo;
  }, [miniSidenav, is_xs]);

  useEffect(() => {
    // Garante o menu da rota atual aberto, sem fechar os demais (estado persiste entre páginas).
    if (collapseName) {
      setOpenCollapses(ensureOpenCollapse(collapseName));
    }
    if (itemParentName) {
      setOpenNestedCollapses(ensureOpenNestedCollapse(itemParentName));
    }
  }, [collapseName, itemParentName]);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    window.addEventListener("resize", handleMiniSidenav);

    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, transparentSidenav, whiteSidenav]);

  const renderNestedCollapse = (collapse: NestedCollapseItem[]) => {
    const template = collapse.map(({ name, route, key, href, disabled, code, icon }) => {
      if (!name) return null;

      if (disabled) {
        return <SidenavItem key={key} color={color} name={name} icon={icon} nested disabled />;
      }

      if (href) {
        return (
          <MuiLink
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} icon={icon} nested />
          </MuiLink>
        );
      }
      if (route) {
        const hasPermission = user?.permissions?.some((permission) => permission.code === code);
        if (!hasPermission) return null;

        return (
          <Link to={route} key={key} style={{ textDecoration: "none" }}>
            <SidenavItem
              color={color}
              name={name}
              icon={icon}
              active={route === pathname || isRouteActive(route)}
              nested
            />
          </Link>
        );
      }
      return null;
    });

    return template;
  };

  const renderCollapse = (collapses: NestedCollapseItem[]) =>
    collapses.map(({ name, code, collapse, route, href, key, icon }) => {
      if (!name) return null;

      let returnValue;

      if (collapse) {
        const hasPermission = !code || user?.permissions?.some((permission) => permission.code === code);
        if (!hasPermission) returnValue = null;
        else {
          returnValue = (
            <SidenavItem
              key={key}
              color={color}
              name={name}
              icon={icon}
              active={key === itemParentName ? "isParent" : false}
              open={openNestedCollapses.includes(key)}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                if (event.currentTarget.classList.contains("MuiListItem-root")) {
                  toggleNestedCollapse(key);
                }
              }}
            >
              {renderNestedCollapse(collapse)}
            </SidenavItem>
          );
        }
      } else if (href) {
        returnValue = (
          <MuiLink
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} icon={icon} active={false} />
          </MuiLink>
        );
      } else if (route) {
        const hasPermission = user?.permissions?.some((permission) => permission.code === code);

        if (!hasPermission) returnValue = null;
        else {
          returnValue = (
            <Link to={route} key={key} style={{ textDecoration: "none" }}>
              <SidenavItem
                color={color}
                name={name}
                icon={icon}
                active={key === itemName || isRouteActive(route)}
              />
            </Link>
          );
        }
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  const renderRoutes = filteredRoutes.map((route) => {
    let returnValue;

    if (route.type === "collapse") {
      const { name, icon, code, key, href, route: routePath, noCollapse, collapse } = route;
      const isContractNow = code === "PLANS";
      if (href) {
        returnValue = (
          <MuiLink
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName || (!!routePath && isRouteActive(routePath))}
              noCollapse={noCollapse}
            />
          </MuiLink>
        );
      } else if (noCollapse && routePath) {
        const hasPermission = user?.permissions?.some(
          (permission) => permission.code === code || isContractNow
        );

        if (!hasPermission) returnValue = null;
        else {
          returnValue = (
            <Link to={routePath} key={key} style={{ textDecoration: "none" }}>
              <SidenavCollapse
                name={name}
                icon={icon}
                noCollapse={noCollapse}
                active={key === collapseName || isRouteActive(routePath)}
                isContractNow={isContractNow}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </Link>
          );
        }
      } else {
        const hasPermission = collapse?.some((collapseItem) =>
          user?.permissions?.some(
            (permission) =>
              permission.code === collapseItem.code ||
              collapseItem.collapse?.some((nested) => nested.code === permission.code)
          )
        );

        if (!hasPermission) returnValue = null;
        else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              active={key === collapseName}
              open={openCollapses.includes(key)}
              onClick={() => toggleCollapse(key)}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      }
    } else if (route.type === "title") {
      const { key, title, shortTitle } = route;
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {miniSidenav ? shortTitle : title}
        </MDTypography>
      );
    } else if (route.type === "divider") {
      const { key } = route;
      returnValue = (
        <Divider
          key={key}
          sx={{
            opacity: 0.25,
            backgroundColor: "transparent",
            backgroundImage:
              "linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff, rgba(255, 255, 255, 0)) !important",
          }}
        />
      );
    }

    return returnValue;
  });

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <CloseIcon sx={{ fontWeight: "bold" }} />
          </MDTypography>
        </MDBox>
        <Link to="/">
          <MDBox display="flex" alignItems="center">
            <MDBox
              component="img"
              src={logoSrc}
              alt={"Talk2Buy"}
              width="100%"
              sx={{
                transition: "none", // Remove transições desnecessárias
                pointerEvents: "none", // Evita eventos de ponteiro desnecessários
              }}
            />
          </MDBox>
        </Link>
      </MDBox>
      <Divider
        sx={{
          opacity: 0.25,
          backgroundColor: "transparent",
          backgroundImage:
            "linear-gradient(to right, rgba(255, 255, 255, 0), #ffffff, rgba(255, 255, 255, 0)) !important",
        }}
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

export default Sidenav;
